import RouteError from '@/components/ui/route-error';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { DICTIONARY } from '@/config/dictionary';
import { APP_PATHS } from '@/config/routes/app-paths';
import DashboardOverviewContent from '@/features/wholesaler-setup/components/dashboard-overview-content';
import SetupLayout from '@/features/wholesaler-setup/components/setup-layout';
import NewUserWelcomeCard from '@/features/wholesaler-setup/components/new-user-welcome-card';
import ActivateStep from '@/features/wholesaler-setup/components/steps/activate';
import BrandingStep from '@/features/wholesaler-setup/components/steps/branding';
import CompanyStep from '@/features/wholesaler-setup/components/steps/company';
import CreditStep from '@/features/wholesaler-setup/components/steps/credit';
import FeaturesStep from '@/features/wholesaler-setup/components/steps/features-step';
import RegionsCarriageStep from '@/features/wholesaler-setup/components/steps/regions-carriage';
import ServicesStep from '@/features/wholesaler-setup/components/steps/services';
import { getOverviewQueryOptions, useOverview } from '@/features/wholesaler-setup/api/overview-api';
import {
  getWarningLevelLabel,
  isBillingWarningValid,
  isCreditWarningValid,
  isCreditRestrictedValid,
} from '@/features/payments/utils/overview-warning-state.ts';
import { useNotifications, getNotificationsQueryOptions } from '@/hooks/use-notifications';
import { useTeamMembers } from '@/features/team/api/team-api';
import { useSetupStore } from '@/features/wholesaler-setup/stores/setup-store';
import type { SetupFormData } from '@/features/wholesaler-setup/types/setup';
import { useAppStore } from '@/stores/app-store';
import { requireAuth } from '@/utils/require-auth-before-load';
import { ensureValidPath } from '@/utils/validate-path';
import { queryClient } from '@/lib/query-client';
import { useDisclosure } from '@/components/ui/lib/use-disclosure';
import {
  useServiceFeatureCatalog,
  getServiceFeatureCatalogQueryOptions,
} from '@/hooks/use-service-feature-catalog';
import type { WholesalerOverview } from '@/types/auth';
import type { WholesalerFeatureConfig, WholesalerServiceConfig } from '@/types/services';

import { formatCurrency } from '@/utils/format-currency';
import { formatActiveItemsSubtext } from '@/utils/format-list';

const DashboardOverviewPage = () => {
  const navigate = useNavigate();
  const currentStep = useSetupStore((state) => state.currentStep);
  const setStep = useSetupStore((state) => state.setStep);
  const updateFormData = useSetupStore((state) => state.updateFormData);
  const fullName = useSetupStore((state) => state.formData.fullName);
  const businessEmail = useSetupStore((state) => state.formData.businessEmail);
  const currentUser = useAppStore((state) => state.currentUser);
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const setSelectedWholesalerId = useAppStore((state) => state.setSelectedWholesalerId);
  const initialWholesalerId = currentUser?.wholesalers[0]?.id ?? null;
  const resolvedWholesalerId = selectedWholesalerId ?? initialWholesalerId;
  const hasAttachedWholesaler = (currentUser?.wholesalers.length ?? 0) > 0;
  const shouldShowSetupFlow = !hasAttachedWholesaler;
  const startNewWholesalerSetup = useSetupStore((state) => state.startNewWholesalerSetup);
  const {
    open: hasClickedCreate,
    onOpen: markHasClickedCreate,
    onClose: unmarkHasClickedCreate,
  } = useDisclosure();
  const { data: overviewWholesaler, isLoading: isOverviewLoading } =
    useOverview(resolvedWholesalerId);
  const { data: notificationsData } = useNotifications(currentUser?.id ?? null);
  const { data: teamData } = useTeamMembers(resolvedWholesalerId);
  const { data: catalog } = useServiceFeatureCatalog();
  const overview = DICTIONARY.dashboard.overview;
  const setup = DICTIONARY.dashboard.setup;
  const selectedWholesaler = overviewWholesaler;

  // Real backend exposes credit as whole USD dollars on WholesalerDto.credit — there is no
  // cents-denominated field. Convert to minor units here since formatCurrency and the
  // warning/restriction threshold comparisons below operate in cents.
  const creditBalanceUsdCents =
    selectedWholesaler?.credit != null ? Math.round(selectedWholesaler.credit * 100) : undefined;
  const warningLevelUsd = selectedWholesaler?.creditThresholds?.warning;
  const restrictionLevelUsd = selectedWholesaler?.creditThresholds?.suspension;
  const isCreditRestrictedVisible = isCreditRestrictedValid(
    creditBalanceUsdCents,
    restrictionLevelUsd,
  );
  const isCreditWarningVisible =
    !isCreditRestrictedVisible && isCreditWarningValid(creditBalanceUsdCents, warningLevelUsd);
  const isKycVerified = selectedWholesaler?.isKycVerified;
  const isBillingWarningVisible = isBillingWarningValid(isKycVerified);
  const recentActivity = (notificationsData?.notifications ?? []).filter(
    (n) => n.scope === 'user' || n.wholesalerId === resolvedWholesalerId,
  );
  const isLoadingSummary = isOverviewLoading;
  const creditLabel =
    creditBalanceUsdCents != null ? formatCurrency(creditBalanceUsdCents, 'usd') : '—';
  const warningLevelLabel = getWarningLevelLabel(warningLevelUsd);
  const wholesaler: WholesalerOverview | undefined = selectedWholesaler;
  const serviceConfigs: WholesalerServiceConfig[] = wholesaler?.serviceConfigs ?? [];
  const featureConfigs: WholesalerFeatureConfig[] = wholesaler?.featureConfigs ?? [];

  const activeServices = serviceConfigs
    .filter((c) => c.visibility !== 'off')
    .map((c) => catalog?.services.find((s) => s.id === c.serviceId)?.name ?? '');
  const activeFeatures = featureConfigs
    .filter((c) => c.enabled)
    .map((c) => catalog?.features.find((f) => f.id === c.featureId)?.name ?? '');
  const teamMembersCount = teamData?.members.length ?? 0;
  const activeServicesSubtext = formatActiveItemsSubtext(activeServices);
  const activeFeaturesSubtext = formatActiveItemsSubtext(activeFeatures);

  useEffect(() => {
    if (selectedWholesalerId == null && initialWholesalerId != null) {
      setSelectedWholesalerId(initialWholesalerId);
    }
  }, [selectedWholesalerId, initialWholesalerId, setSelectedWholesalerId]);

  useEffect(() => {
    if (shouldShowSetupFlow && currentStep === 'personal-details') {
      setStep('company');
    }
  }, [shouldShowSetupFlow, currentStep, setStep]);

  useEffect(() => {
    if (currentUser == null || !shouldShowSetupFlow) {
      return;
    }

    const nextData: Partial<SetupFormData> = {};

    if (fullName.length === 0) {
      nextData.fullName = currentUser.displayName;
    }

    if (businessEmail.length === 0) {
      nextData.businessEmail = currentUser.email;
    }

    if (Object.keys(nextData).length > 0) {
      updateFormData(nextData);
    }
  }, [currentUser, businessEmail, fullName, shouldShowSetupFlow, updateFormData]);

  if (shouldShowSetupFlow && !hasClickedCreate) {
    return (
      <DashboardRouteLayout>
        <DashboardPageShell
          title={setup.welcomeCard.heading(
            currentUser?.firstName ?? currentUser?.displayName ?? '',
          )}
          description={setup.welcomeCard.bodyFallback}
          allowOverflow
        >
          <NewUserWelcomeCard
            onCreateProfile={() => {
              startNewWholesalerSetup(undefined, true);
              markHasClickedCreate();
            }}
          />
        </DashboardPageShell>
      </DashboardRouteLayout>
    );
  }

  if (shouldShowSetupFlow) {
    const resolvedStep = currentStep === 'personal-details' ? 'company' : currentStep;

    return (
      <DashboardRouteLayout>
        <SetupLayout currentStep={resolvedStep}>
          <DashboardPageShell title={setup.heading} description={setup.body}>
            {resolvedStep === 'company' && <CompanyStep onBack={unmarkHasClickedCreate} />}
            {resolvedStep === 'regions-carriage' && <RegionsCarriageStep />}
            {resolvedStep === 'services' && <ServicesStep />}
            {resolvedStep === 'features' && <FeaturesStep />}
            {resolvedStep === 'credit' && <CreditStep />}
            {resolvedStep === 'branding' && <BrandingStep />}
            {resolvedStep === 'activate' && <ActivateStep />}
          </DashboardPageShell>
        </SetupLayout>
      </DashboardRouteLayout>
    );
  }

  return (
    <DashboardRouteLayout>
      <DashboardPageShell title={overview.heading} description={overview.body}>
        <DashboardOverviewContent
          creditLabel={creditLabel}
          warningLevelLabel={warningLevelLabel}
          isCreditRestrictedVisible={isCreditRestrictedVisible}
          isCreditWarningVisible={isCreditWarningVisible}
          isBillingWarningVisible={isBillingWarningVisible}
          isLoadingSummary={isLoadingSummary}
          isOverviewLoading={isOverviewLoading}
          activeServicesCount={activeServices.length}
          activeServicesSubtext={activeServicesSubtext}
          activeFeaturesCount={activeFeatures.length}
          activeFeaturesSubtext={activeFeaturesSubtext}
          teamMembersCount={teamMembersCount}
          recentActivity={recentActivity}
          onGoToBilling={() => {
            void navigate({ to: ensureValidPath(APP_PATHS.billing) });
          }}
          onGoToKyc={() => {
            void navigate({ to: ensureValidPath(APP_PATHS.wholesalerSettings) });
          }}
        />
      </DashboardPageShell>
    </DashboardRouteLayout>
  );
};

const overviewSearchSchema = z.object({
  page: z.number().int().min(1).catch(1),
});

export const Route = createFileRoute('/overview')({
  validateSearch: overviewSearchSchema,
  beforeLoad: requireAuth,
  loader: async () => {
    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const wholesalerId = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id;

    if (wholesalerId == null) {
      return null;
    }

    const userId = currentUser?.id;
    await Promise.allSettled([
      queryClient.prefetchQuery(getOverviewQueryOptions(wholesalerId)),
      queryClient.prefetchQuery(getServiceFeatureCatalogQueryOptions(wholesalerId)),
      ...(userId != null ? [queryClient.prefetchQuery(getNotificationsQueryOptions(userId))] : []),
    ]);
    return null;
  },
  component: DashboardOverviewPage,
  errorComponent: RouteError,
});
